import React, { FC, memo } from 'react';

import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { PolicyAggregate, policySettingIdToI18Key } from '../../utils';
import { getPoliciesToDisplay } from './helpers';
import './index.scss';

const nameSpace = 'policy';

interface PolicyDescriptionProps {
    policyAggregate: PolicyAggregate;
    isPublic?: boolean;
}

const EMAIL_LIST_VALUES = ['autoSharing_Value'];
/**
 * Non-boolean settings normally append the days unit (legacy PolicyDescription
 * behavior — e.g. dataRetention summary). Settings in this list show a plain
 * value instead, so new string settings do not get a bogus "days" suffix.
 */
const SETTINGS_WITHOUT_DAY_SUFFIX = [
    'autoApplyPurviewLabel.Name',
];

const PolicyDescription: FC<PolicyDescriptionProps> = ({ policyAggregate, isPublic = false }) => {
    const { t } = useTranslation();

    const policySettingsToDisplay = getPoliciesToDisplay(policyAggregate, isPublic);

    return (
        <div className="file-policy-wrap">
            <ul>
                {
                    policySettingsToDisplay.map((policy) => {
                        const policyType = policy.type;
                        const value = policyType === 'checkbox' ? Object.keys(policy.value) : policy.value;

                        const policyKey = policySettingIdToI18Key(policy.id);
                        const isIncludedInEmailListValues = EMAIL_LIST_VALUES.includes(policyKey);
                        const isBoolean = (typeof value === 'boolean' && policyType !== 'input')
                            || isIncludedInEmailListValues;
                        const skipDaySuffix = SETTINGS_WITHOUT_DAY_SUFFIX.includes(policy.id);
                        const policyDescription = isIncludedInEmailListValues && Array.isArray(policy.value)
                            ? policy.value.join(', ')
                            : t(`${nameSpace}.${policyKey}.title`, { defaultValue: policy.description });

                        // Skip items with no description text
                        if (!policyDescription) return null;

                        const hasPlainValue = !isBoolean
                            && skipDaySuffix
                            && value !== undefined
                            && value !== null
                            && value !== '';

                        const listClassname = classNames('description', { indented: policy.dependency });
                        return (
                            <li key={policy.id} className={listClassname}>
                                <LockOutlined />
                                <div className="check-text">
                                    {isBoolean && policyDescription}
                                    {!isBoolean && skipDaySuffix && (
                                        <>
                                            {policyDescription}
                                            {hasPlainValue && (
                                                <>
                                                    {' '}
                                                    <strong>
                                                        (
                                                        {String(policy.value)}
                                                        )
                                                    </strong>
                                                </>
                                            )}
                                        </>
                                    )}
                                    {!isBoolean && !skipDaySuffix && (
                                        <>
                                            {policyDescription}
                                            {' '}
                                            <strong>
                                                {t('general.time.days__withBrackets', { value: policy.value })}
                                            </strong>
                                        </>
                                    )}
                                </div>
                            </li>
                        );
                    })
                }
                <li className="description">
                    <LockOutlined />
                    <div className="check-text">{t(`${nameSpace}.default.cloudAppProtection`)}</div>
                </li>
            </ul>
        </div>
    );
};

export default memo(PolicyDescription);
